import React, { useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Mic, MicOff, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const VoiceRecorder = ({ onSend, onCancel }) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [sending, setSending] = useState(false);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl
  } = useReactMediaRecorder({
    audio: true,
    onStart: () => {
      const interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
      setTimerInterval(interval);
    },
    onStop: () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
    }
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setRecordingTime(0);
    startRecording();
  };

  const handleStop = () => {
    stopRecording();
  };

  const handleSend = async () => {
    if (!mediaBlobUrl || sending) return;
    setSending(true);

    try {
      const response = await fetch(mediaBlobUrl);
      const blob = await response.blob();
      const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
      
      await onSend(file, recordingTime);
      clearBlobUrl();
      setRecordingTime(0);
    } catch (error) {
      toast.error('Failed to send voice message');
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    clearBlobUrl();
    setRecordingTime(0);
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    if (onCancel) onCancel();
  };

  if (status === 'idle' || status === 'stopped') {
    return (
      <div className="flex items-center gap-2 p-3 bg-white border border-[#E2E8F0] rounded-md">
        {mediaBlobUrl ? (
          <>
            <audio src={mediaBlobUrl} controls className="flex-1" data-testid="voice-preview" />
            <span className="text-sm text-[#475569]">{formatTime(recordingTime)}</span>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSend}
              disabled={sending}
              data-testid="voice-send-button"
            >
              <Send className="w-5 h-5 text-[#4338CA]" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleCancel}
              data-testid="voice-cancel-button"
            >
              <X className="w-5 h-5" />
            </Button>
          </>
        ) : (
          <Button
            onClick={handleStart}
            className="flex items-center gap-2"
            data-testid="voice-start-button"
          >
            <Mic className="w-5 h-5" />
            Start Recording
          </Button>
        )}
      </div>
    );
  }

  if (status === 'recording') {
    return (
      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="voice-recording">
        <div className="flex items-center gap-2 flex-1">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-red-700">Recording...</span>
          <span className="text-sm text-red-600">{formatTime(recordingTime)}</span>
        </div>
        <Button
          size="icon"
          variant="destructive"
          onClick={handleStop}
          data-testid="voice-stop-button"
        >
          <MicOff className="w-5 h-5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleCancel}
          data-testid="voice-cancel-recording-button"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>
    );
  }

  return null;
};

export default VoiceRecorder;
